import { useState } from 'react';

import { Check, Copy, Mail, ShieldQuestion } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';

import styles from './AdministratorDialog.module.css';

/**
 * Who to ask for a password reset.
 *
 * Static for now, like the rest of the brand text: the company profile needs a
 * session to read, and nobody opening this dialog has one. Phase 11 wires it to the
 * organisation's public email through a branding endpoint.
 */
const ADMINISTRATOR_EMAIL = 'administrator@flyst.com';

export function AdministratorDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    // Clipboard access is denied outside a secure context and missing entirely in
    // some embedded browsers. The address is on screen either way, so a failure is
    // not worth an error message.
    try {
      await navigator.clipboard.writeText(ADMINISTRATOR_EMAIL);
    } catch {
      // Ignored on purpose. The user can still select and copy it by hand.
    }
    setCopied(true);
    window.setTimeout(() => {
      setCopied(false);
    }, 1800);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={<ShieldQuestion size={19} />}
      title="Password resets go through your administrator"
      description="Accounts are managed centrally, so passwords cannot be reset from this screen."
      footer={
        <Button variant="ghost" block onClick={onClose}>
          Back to sign in
        </Button>
      }
    >
      <div className={styles.row}>
        <Mail size={15} aria-hidden="true" className={styles.icon} />
        <span className={styles.address}>{ADMINISTRATOR_EMAIL}</span>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            void copy();
          }}
          icon={copied ? <Check size={13} /> : <Copy size={13} />}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className={styles.note}>
        Include your full name and work email so the administrator can identify your account.
        They will send you a link to set a new password.
      </p>
    </Modal>
  );
}
